import React, { useState, useEffect } from 'react';
import { C } from './UI';

interface Props {
    label: string;
    value: string;
    onChange: (value: string) => void;
    required?: boolean;
}

export function DateTimePicker({ label, value, onChange, required }: Props) {
    const today = new Date();
    const minDate = today.toISOString().split('T')[0];
    const maxDate = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    const parseValue = (val: string) => {
        if (!val) return { date: '', time: '' };
        if (val.includes('T')) {
            const [d, t] = val.split('T');
            return { date: d, time: t.slice(0, 5) };
        }
        return { date: '', time: '' };
    };

    const { date: initDate, time: initTime } = parseValue(value);
    const [selectedDate, setSelectedDate] = useState(initDate);
    const [selectedTime, setSelectedTime] = useState(initTime);

    // Sync internal state when parent resets the value (e.g. navigating back to step 1)
    useEffect(() => {
        const { date, time } = parseValue(value);
        setSelectedDate(date);
        setSelectedTime(time);
    }, [value]);

    // Always emit ISO format so parseValue can round-trip correctly
    const handleDateChange = (d: string) => {
        setSelectedDate(d);
        if (d && selectedTime) {
            onChange(`${d}T${selectedTime}`);
        }
    };

    const handleTimeChange = (t: string) => {
        setSelectedTime(t);
        if (selectedDate && t) {
            onChange(`${selectedDate}T${t}`);
        }
    };

    const formatDisplay = (d: string, t: string) => {
        if (!d || !t) return '';
        const dt = new Date(`${d}T${t}`);
        const dateStr = dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        const timeStr = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
        return `${dateStr} at ${timeStr}`;
    };

    const inputStyle: React.CSSProperties = {
        flex: 1,
        padding: '10px 12px',
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        fontSize: 14,
        color: C.text,
        outline: 'none',
        fontFamily: 'Inter, sans-serif',
        background: '#fff',
        boxSizing: 'border-box',
    };

    return (
        <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 }}>
                {label}
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
                <input
                    type="date"
                    value={selectedDate}
                    min={minDate}
                    max={maxDate}
                    onChange={e => handleDateChange(e.target.value)}
                    required={required}
                    style={inputStyle}
                />
                <input
                    type="time"
                    value={selectedTime}
                    onChange={e => handleTimeChange(e.target.value)}
                    required={required}
                    style={inputStyle}
                />
            </div>
            {selectedDate && selectedTime && (
                <div style={{ fontSize: 12, color: C.greenMid, marginTop: 5, fontWeight: 500 }}>
                    ✓ {formatDisplay(selectedDate, selectedTime)}
                </div>
            )}
            {(!selectedDate || !selectedTime) && (
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
                    Select a date and time for your pickup
                </div>
            )}
        </div>
    );
}
